
import { Component, Vue, Emit, Watch, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { vuex } from '@/store';
import rest from '@/rest';
import { watch } from 'vue';
import { List } from 'linq-collections';
import { signalrEventBus } from "@/main";
// import draggable from 'vuedraggable';
import { l3draggable, l3droppable, DraggableOptions } from "@/directives/l3dragDrop";
// components
import TusFileDrop from '../Shared/TusFileDrop.vue';
import CompAudioPlayerWithSubtitles from '../ExerciseComponents/CompAudioPlayerWithSubtitles.vue';
import MarkdownEditor from '../Shared/MarkdownEditor.vue';
// interfaces
import { ExBundleVM } from '@/interfaces/ExBundleAssignmentGroupVM';
import { FileDetailsVm } from '@/interfaces/FileDetailsVm';
import { ParamDictionary } from '@/interfaces/ParamDictionary';
import { CustomizationVm } from '@/interfaces/CustomizationVm';
import CompVideo from '../ExerciseComponents/CompVideo.vue';
import SimpleMessageBox from '../Shared/SimpleMessageBox.vue';
import DocumentMediaExtractionDlg from './DocumentMediaExtractionDlg.vue';

const globals = namespace("globals");
const ux = namespace("ux");
const customization = namespace("customization");

@Component({
  components: {
    // draggable
    TusFileDrop,
    CompAudioPlayerWithSubtitles,
    CompVideo,
    MarkdownEditor,
    SimpleMessageBox,
    DocumentMediaExtractionDlg
  },
  directives: { l3draggable, l3droppable }
})
export default class MediaPool extends Vue {
  @Prop({ default: () => [] }) initialSelectedFiles!: string[];
  @Prop({ default: false }) enableSelection!: boolean;
  @Prop({ default: false }) multiDelete!: boolean;

  @customization.Getter customizationSettings!: CustomizationVm;
  @globals.Getter selectedBundle!: ExBundleVM;
  @ux.Getter gradientBackground2: any;

  showFileList: boolean = true; // Used to force reload of file list
  showMarkdownEditor: boolean = false;
  showDocumentMediaExtractionDlg: boolean = false;
  selectedLanguageCode = "de-DE";
  selectedFile: FileDetailsVm | null = null;
  multiDeleteActive: boolean = false;
  aiUpdateInProgress: boolean = false;
  bundleFileList: FileDetailsVm[] | null = null;

  clearMultiDeleteSelection() {
    this.bundleFileList?.forEach(f => f.isSelected = false);
  }

  // Context menu
  selectedType: string = "";
  contextMenuX: number = 0;
  contextMenuY: number = 0;
  showMenu: boolean = false;
  selectedFileForContextMenu: FileDetailsVm | null = null;
  showConfirmDeleteFile: boolean = false;

  async mounted() {
    // this.bundleFileList = await this.loadBundleFiles();
    // let filesToSelect = new List(this.initialSelectedFiles);
    // if (this.bundleFileList)
    //   new List(this.bundleFileList).where(f => filesToSelect.contains(f.filePath!)).forEach(f => f.isSelected = true);
    await this.updateBundleFiles();
  }

  created() {
    // SignalR event listener
    signalrEventBus.$on('backgroundTaskEvent', this.backgroundTaskEvent);
    this.$root.$on('fileListUpdate:MediaPool', this.updateBundleFiles); // Register global event listener
  }

  beforeDestroy() {
    // clearAllBodyScrollLocks();
    // Make sure to cleanup SignalR event handlers when removing the component
    signalrEventBus.$off('backgroundTaskEvent', this.backgroundTaskEvent);
    this.$root.$off('fileListUpdate:MediaPool', this.updateBundleFiles);
  }

  // SignalR events
  async backgroundTaskEvent(response: { id: string, progress: number, message: string, status: string }) {
    console.log("MediaPool > Background task event:", response);
    if (response.status == "INF") {
      return;
    }

    // Reload files
    let newFileList = await this.loadBundleFiles();
    // console.log("New file list after background task:", newFileList);

    let newFileInfo = newFileList.find(file => file.name == response.id);
    // console.log("New file info for background task:", newFileInfo);
    if (!newFileInfo) return;

    let finishedFile = this.bundleFileList?.find(file => file.name == response.id);
    // console.log("Finished file to update:", finishedFile);
    if (!finishedFile) return;

    // Update finished file details
    finishedFile.hasAiSummary = newFileInfo.hasAiSummary;
    finishedFile.aiSummary = newFileInfo.aiSummary;
    finishedFile.isBusy = false; // Reset busy state

    // Force reload of audio and videoplayer
    this.showFileList = false;
    await this.$globalHelper.delay(10);
    this.showFileList = true;
  }

  async updateBundleFiles () {
    this.bundleFileList = await this.loadBundleFiles();
    let filesToSelect = new List(this.initialSelectedFiles);
    if (this.bundleFileList) {
      this.bundleFileList.forEach(f => {
        const fileName = this.$globalHelper.extractFileName(f.filePath!);
        f.isSelected = filesToSelect.contains(fileName);
      });
      // console.log("MediaPool > Updated bundle file list:", this.bundleFileList);
      this.onSelectionchange(); // Emit selection change event after updating file list
    }
  }

  showContextMenu (e, file: FileDetailsVm) {
    e.preventDefault();
    this.selectedFileForContextMenu = file;

    this.showMenu = false;
    this.contextMenuX = e.clientX;
    this.contextMenuY = e.clientY;
    this.$nextTick(() => {
      this.showMenu = true;
    });
  }

  // @Watch('showMenu')
  // onShowMenuChanged(val: boolean) {
  //   if (!val) this.selectedFileForContextMenu = null;
  // }

  onConfirmDeleteFile() {
    this.showConfirmDeleteFile = true;
  }

  onConfirmDeleteMultipleFiles() {
    this.selectedFileForContextMenu = null;
    this.showConfirmDeleteFile = true;
  }

  async deleteFile() {
    if (this.selectedFileForContextMenu) {
      await this.onDeleteFileOrDirectory(this.selectedFileForContextMenu!);
      await this.updateBundleFiles();
      return;
    }

    if (this.multiDeleteActive) {
      await this.onDeleteSelectedItems();
      this.multiDeleteActive = false;
      await this.updateBundleFiles();
      return;
    }
  }

  async onDeleteSelectedItems(): Promise<void> {
    if (this.bundleFileList == null) return;

    // let selectedFiles = this.filteredBundleFileList.filter((file: FileDetailsVm) => file.isSelected);
    for (const file of this.getSelectedFiles()) {
        await this.onDeleteFileOrDirectory(file);
    }
  }

  async onDeleteFileOrDirectory(file: FileDetailsVm): Promise<void> {
    if (!file) return;
    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["BundleId"] =  this.selectedBundle.id.toString();
    params.dictionary!["LocalPath"] = this.selectedBundle.localPath ?? "";
    params.dictionary!["RelativePath"] = "";
    params.dictionary!["FileName"] = file.name ?? "";
    params.dictionary!["IsDirectory"] = file.isDirectory.toString();

    await rest.url("contentManager/deleteFileOrDirectoryFromBundle").post(params);
  }

  onDownloadFile() {
    if (!this.selectedFileForContextMenu) return;
    let filename = this.$globalHelper.extractFileName(this.selectedFileForContextMenu.filePath!);
    this.$globalHelper.download(this.selectedFileForContextMenu.filePath!, filename);
  }

  onDocumentMediaExtraction() {
    this.showDocumentMediaExtractionDlg = true;
  }

  async onCreateAiSummaryForPresentation(file: FileDetailsVm) {
    if (!file) return;

    file.isBusy = true;

    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["FilePath"] = file.filePath ?? "";
    params.dictionary!["ExtractImages"] = "false";
    params.dictionary!["ImageSizeMin"] = "0";
    params.dictionary!["ImageSizeMax"] = "0";
    params.dictionary!["ExtractAudio"] = "false";
    params.dictionary!["ExtractVideo"] = "false";
    params.dictionary!["CreateMarkdownSummary"] = "true";
    // params.dictionary!["CreateAiSummary"] = "true";

    try {
      let success = await rest.url("contentManager/extractMediaFilesFromDocument").post(params);

      if (success) {
        // Update MediaPool
       await this.updateBundleFiles();
      } else {
      }
    } finally {
      file.isBusy = false;
    }
  }

  dragOptions(file: FileDetailsVm) {
    let targetGroups: string[] = [ "file-details" ];
    if (file.fileType == "Image") targetGroups.push("image-file-details");
    if (file.fileType == "Audio") targetGroups.push("audio-file-details");
    if (file.fileType == "Video") targetGroups.push("video-file-details");
    if (file.fileType == "Pdf") targetGroups.push("pdf-file-details");
    if (file.fileType == "Markdown" || file.fileType == "Text") targetGroups.push("markdown-file-details");

    return <DraggableOptions>{
      data: file,
      dragClass: "dragging",
      effect: "all",
      targetGroups: targetGroups,
      onDragStart: (ev: DragEvent) => {
        console.log("Drag started for file:", file.name);
      },
      // onDragEnd: () => this.$emit("drag-end", this.element)
    };
  }

  async loadBundleFiles(): Promise<FileDetailsVm[]> {
    // console.log("Loading bundle files for bundle:", this.selectedBundle);
    if (this.selectedBundle == null) return [];

    let fileListParam: ParamDictionary = { dictionary: {}};
    fileListParam.dictionary!["BundleId"] = this.selectedBundle.id.toString();
    fileListParam.dictionary!["Type"] = "All";
    return  await rest.url("contentManager/getBundleFiles").post(fileListParam);
  }

  get filteredBundleFileList(): FileDetailsVm[] {
    if (this.bundleFileList == null) return [] as FileDetailsVm[];
    if (this.selectedType == "All") return this.bundleFileList;
    if (this.selectedType == "Document") {
      return this.bundleFileList.filter((file: FileDetailsVm) => file.fileType == "Document" || file.fileType == "Presentation" || file.fileType == "Spreadsheet" || file.fileType == "Pdf" || file.fileType == "Text" || file.fileType == "Markdown");
    }
    return this.bundleFileList.filter((file: FileDetailsVm) => file.fileType == this.selectedType);
  }

  get itemSelected(): boolean {
    // if (this.bundleFileList == null) return false;
    return this.filteredBundleFileList.some((file: FileDetailsVm) => file.isSelected);
  }

  get selectionCount(): number {
    if (this.bundleFileList == null) return 0;
    return this.filteredBundleFileList.filter((file: FileDetailsVm) => file.isSelected).length;
  }

  get fileOrFilesDeletionTitle(): string {
    if (this.selectedFileForContextMenu)
      return "Datei löschen";

    const count = this.selectionCount;
    if (count === 1) {
      return "1 Datei löschen";
    } else {
      return `${count} Dateien löschen`;
    }
  }

  get fileDeletionWarningText(): string {
    if (this.selectedFileForContextMenu)
      return `Wollen Sie diese Datei wirklich löschen?<br /><br /> ${this.selectedFileForContextMenu.name}`;

    const count = this.selectionCount;
    let msg = "";
    if (count === 1) {
      msg = "Wollen Sie die ausgewählte Datei wirklich löschen?<br /><br />";
    } else {
      msg = `Wollen Sie die ${count} ausgewählten Dateien wirklich löschen?<br /><br />`;
    }
    this.getSelectedFiles().forEach(element => {
      msg += `${element.name}<br />`;
    });

    return msg;
  }

  public getSelectedFiles(): FileDetailsVm[] {
    // if (this.bundleFileList == null) return [] as FileDetailsVm[];
    const selectedFiles = this.filteredBundleFileList.filter((file: FileDetailsVm) => file.isSelected);
    // console.log("MediaPool > Selected files:", selectedFiles);
    return selectedFiles;
  }

  onSelectionchange() {
    this.$emit("selectionChanged:mediaPool");
  }

  onEditMarkdown(file) {
    this.selectedFile = file;
    this.showMarkdownEditor = true;
  }

  async onMediaFileUploaded(response: any): Promise<void> {
    if (!response.success) {
      this.$store.commit("ux/SB_FAILURE", {
        message: response.message,
        timeout: 0
      });
      return;
    }

    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["OldFilePath"] = "";
    params.dictionary!["UploadedFileName"] = response.fileName!;
    params.dictionary!["RelativePath"] = "";
    params.dictionary!["BundleId"] = this.selectedBundle.id!.toString();
    await rest.url("contentManager/moveFileToBundleDir").post(params)
      .then(async (response) => {
        if (!response) {
          // vuex.globals.setBundle(null);
          // await this.updateBundles();
          return;
        }
        // console.log("response", response);
        this.bundleFileList = null;
        this.bundleFileList = await this.loadBundleFiles();
      })
      .catch(err => {
      })
      .finally(() => {
      });
  }

  enableAiFeatures(file: FileDetailsVm): boolean {
    return this.customizationSettings.enableAi && (file.fileType == "Pdf" || file.fileType == "Image" || file.fileType == "Audio" || file.fileType == "Video");
  }

  async onCreateAiSummary(file: FileDetailsVm): Promise<void> {
    this.aiUpdateInProgress = true;

    await this.$l3rnOnlineHub.ensureConnection();
    file.isBusy = true;

    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["SignalRId"] = this.$l3rnOnlineHub.ConnectionId!;
    params.dictionary!["BundleId"] = this.selectedBundle.id!.toString();
    params.dictionary!["FileName"] = file.name!;
    params.dictionary!["LanguageCode"] = this.selectedLanguageCode || "de-DE";

    await rest.url("contentManager/createAiSummary").post(params)
      .then(async (response) => {
        if (!response) return;
        // console.log("response", response);
      })
      .catch(err => {
      })
      .finally(() => {
        this.aiUpdateInProgress = false;
      });
  }

  async onCreateMissingAiSummary(): Promise<void> {
    if (this.bundleFileList == null) return;
    let filesWithoutAiSummary = this.bundleFileList.filter((file: FileDetailsVm) => file.isSelected && !file.hasAiSummary);
    for (const file of filesWithoutAiSummary) {
      await this.onCreateAiSummary(file);
    }
  }

  get focusStyle() {
    return {
      '--focus-border-color': this.$store.state.ux.colorAccent,
      '--focus-bg-color': this.$store.state.ux.colorAccent + '20' // Add transparency
    };
  }

}
